import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import AllMembers from "@/src/components/groups/members/all-members";
import PendingMembers from "@/src/components/groups/members/pending-members";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import ScTabs from "@/src/components/ui-kit/tabs";
import ToolbarCreateButton from "@/src/components/ui-kit/toolbar-create-button";
import { MembershipStatus } from "@/src/constants/groups/group-membership";
import { useAuth } from "@/src/contexts/auth.context";
import { useGroupMembersTable } from "@/src/hooks/features/groups/use-group-members-table";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image, Text, View } from "react-native";
import { useTheme } from "@/src/styles/theme-provider";

export type PagyMeta = Readonly<{
  current_page: number;
  total_pages: number;
  total_count: number;
  from: number;
  to: number;
}>;

export type GroupMembersTableProps = Readonly<{
  groupId: string;
  isMember: boolean;
  isAdmin: boolean;
  addMembership: (membershipId: string) => void;
  handleRemoveMembership: (
    membershipId: string,
    toastKey: string,
    removedUserId?: string, 
  ) => void;
  isActionPending: boolean;
  onAdd: () => void;
  initialMembersTab?: string;
}>;

export enum GroupMemberTab {
  ALL_MEMBERS = "LABEL_ALL_USERS",
  PENDING = "LABEL_PENDING",
}

const GroupMembersTable = ({
  groupId,
  isMember,
  isAdmin,
  addMembership,
  handleRemoveMembership,
  isActionPending,
  onAdd,
  initialMembersTab,
}: GroupMembersTableProps) => {
  const tabs: GroupMemberTab[] = [
    GroupMemberTab.ALL_MEMBERS,
    ...(isAdmin ? [GroupMemberTab.PENDING] : []),
  ];

  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { user } = useAuth();
  const {
    active,
    setActive,
    members,
    membersPagy,
    setMembersPage,
    isPending,
    isError,
    confirmOpen,
    actionType,
    setConfirmOpen,
    handleConfirm,
  } = useGroupMembersTable(groupId, initialMembersTab);

  const [search, setSearch] = useState("");
  const filteredMembers = members.filter(
    (member) =>
      `${member.firstName} ${member.lastName}`
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      member.email.toLowerCase().includes(search.toLowerCase()),
  );

  if (isError) {
    return (
      <View className="p-4 flex-col items-center justify-center">
        <Image
          source={require("@/src/assets/images/error.png")}
          style={{ resizeMode: "contain", width: 300, height: 300 }}
        />
        <Text className="mt-2 text-center font-medium text-xl text-red-light">
          {t("ERROR_LOADING_GROUP_MEMBERS")}
        </Text>
      </View>
    );
  }

  return (
    <View className="min-h-[60%] justify-start gap-2 md:bg-chalk md:rounded-2xl md:border md:border-ash-3 md:p-4">
      <View className="flex-row gap-2 justify-between items-center">
        <ScTabs tabs={tabs} active={active} onChange={setActive} />
        {isMember && (
          <ToolbarCreateButton text="BTN_ADD_MEMBERS" onPress={onAdd} />
        )}
      </View>
      <View className="pt-4 pb-2 w-full md:max-w-96">
        <ScSearchInput
          value={search}
          placeholderKey="LABEL_SEARCH_USERS"
          onChangeText={setSearch}
          surface="screen"
        />
      </View>

      {active === GroupMemberTab.ALL_MEMBERS && (
        <AllMembers
          members={filteredMembers.filter(
            (member) => member.status == MembershipStatus.ACTIVE,
          )}
          membersPagy={membersPagy}
          onMembersPageChange={setMembersPage}
          groupId={groupId}
          isAdmin={isAdmin}
          isActionPending={isActionPending}
          handleRemoveMembership={handleRemoveMembership}
          isLoading={isPending}
        />
      )}

      {active === GroupMemberTab.PENDING && (
        <PendingMembers
          pendingMembers={filteredMembers.filter(
            (member) => member.status == MembershipStatus.PENDING,
          )}
          onPendingPageChange={() => {}}
          addMembership={addMembership}
          handleRemoveMembership={handleRemoveMembership}
          isActionPending={isActionPending}
          isLoading={isPending}
        />
      )}

      <ConfirmModal
        visible={confirmOpen}
        title={actionType === "delete" ? t("QUEST_DELETE_MEMBER") : ""}
        description={
          actionType === "delete" ? t("LABEL_DESC_DELETE_MEMBER") : ""
        }
        confirmText={actionType === "delete" ? t("BTN_DELETE") : ""}
        cancelText={t("BTN_CANCEL")}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </View>
  );
};

export default GroupMembersTable;
